import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Skeleton from '../common/Skeleton';
import { Save, Check, CircleMinus, CircleCheck, Lock, Edit2 } from 'lucide-react';
import riskImpactsService, { RiskImpact } from '../../services/riskImpactsService';
import Modal from '../common/Modal';
import Button from '../common/Button';
import FormControl from '../common/FormControl';
import Pagination from '../common/Pagination';
import Toast, { ToastMessage } from '../common/Toast';
import { confirmWarning, confirmActive } from '../common/confirm';
import extractApiError from '../../utils/apiError';
import NoDataFound from '../common/NoDataFound';
import ApprovalDot from '../common/ApprovalDot';
import WorkflowModal from '../common/WorkflowModal';
import { usePermission } from '../../contexts/PermissionContext';
import { RBAC_ACTIVITY } from './rbacMock';
import { useFieldValidation } from '../../validation/useFieldValidation';
import { ValidatedInput, ValidatedTextarea } from '../../validation/ValidatedFields';
import { isUniqueLocal } from '../../validation/uniqueness';

interface RiskImpactsTabProps {
  createTrigger?: number;
  pageSize: number;
  onPageSizeChange?: (size: number) => void;
  searchTerm: string;
  statusFilter: string;
}

type WorkflowRecord = { workflow_state: 'draft' | 'submitted' | 'approved' | 'rejected' };
type RowWithWorkflow = RiskImpact & WorkflowRecord;

const RiskImpactsTab: React.FC<RiskImpactsTabProps> = ({ createTrigger, pageSize, onPageSizeChange, searchTerm, statusFilter }) => {
  const viewPerm = usePermission(RBAC_ACTIVITY, 'view');
  const modifyPerm = usePermission(RBAC_ACTIVITY, 'view+modify');
  const canView = viewPerm.allowed;
  const canModify = modifyPerm.allowed;
  const permsLoading = viewPerm.loading;
  // Note: approve/reject/reopen gating is resolved by the WF engine inside
  // <WorkflowModal> (it returns per-action permissions), so no canApprove here.

  const [impacts, setImpacts] = useState<RowWithWorkflow[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [selectedImpact, setSelectedImpact] = useState<RowWithWorkflow | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  // View-first edit: existing records open read-only; "Edit" enables the form.
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<Partial<RiskImpact>>({});
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [toggling, setToggling] = useState(false);
  const fieldVh = useFieldValidation();

  const [workflowOpenFor, setWorkflowOpenFor] = useState<RowWithWorkflow | null>(null);

  const loadImpacts = useCallback(async () => {
    if (!canView) return;
    try {
      setLoading(true);
      const data = await riskImpactsService.getAll({
        status: statusFilter as any,
        search: searchTerm || undefined,
        limit: 100
      });
      const sorted = [...data].sort((a, b) => (b.id || 0) - (a.id || 0));
      setImpacts(sorted as RowWithWorkflow[]);
    } catch (err: any) {
      setToast({ type: 'error', message: err.message || 'Failed to load risk impacts' });
    } finally {
      setLoading(false);
    }
  }, [statusFilter, searchTerm, canView]);

  useEffect(() => { loadImpacts(); }, [loadImpacts]);
  useEffect(() => { setPage(1); }, [searchTerm, statusFilter, pageSize]);
  useEffect(() => { if (createTrigger && canModify) handleAddNew(); }, [createTrigger]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    setSubmitting(false);
    setToggling(false);
  }, [showModal]);

  const handleRowClick = (impact: RowWithWorkflow) => {
    setSelectedImpact(impact);
    setFormData(impact);
    fieldVh.reset();
    setIsEditMode(true);
    setIsEditing(false); // open read-only; "Edit" enables the form
    setShowModal(true);
  };

  const handleAddNew = () => {
    if (!canModify) return;
    setSelectedImpact(null);
    setFormData({ impact_name: '', impact_description: '', is_active: 1 });
    fieldVh.reset();
    setIsEditMode(false);
    setIsEditing(true); // create is always editable
    setShowModal(true);
  };

  const isApproved = selectedImpact?.workflow_state === 'approved';
  const fieldsDisabled = isEditMode && (!isEditing || !canModify || isApproved);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (fieldsDisabled) return;
    const ok = fieldVh.validateAll([
      { name: 'impact_name', code: 109, value: formData.impact_name || '', required: true },
      { name: 'impact_description', code: 110, value: formData.impact_description || '' },
    ]);
    if (!ok) return;
    if (!isUniqueLocal(908, (formData.impact_name || ''), impacts, selectedImpact?.id)) {
      fieldVh.setError('impact_name', 'Impact with this name already exists');
      return;
    }
    setSubmitting(true);
    try {
      if (isEditMode && selectedImpact) {
        const { is_active: _ia, ...updateData } = formData;
        await riskImpactsService.update(selectedImpact.id, updateData);
        setToast({ type: 'success', message: 'Risk Impact updated successfully.' });
      } else {
        await riskImpactsService.create(formData);
        setToast({ type: 'success', message: 'Risk Impact created successfully.' });
      }
      setShowModal(false);
      loadImpacts();
    } catch (err: any) {
      const msg = extractApiError(err, 'Operation failed');
      if (/exist|duplicate|unique/i.test(msg)) {
        fieldVh.setError('impact_name', msg);
      } else {
        setToast({ type: 'error', message: msg });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async () => {
    if (!selectedImpact || !canModify || isApproved) return;
    const next = selectedImpact.is_active ? 0 : 1;
    const confirmFn = selectedImpact.is_active ? confirmWarning : confirmActive;
    const ok = await confirmFn({
      title: next ? 'Reactivate this impact?' : 'Inactivate this impact?',
      text: next ? 'It will appear in active lists again.' : 'It will be hidden from active lists.',
      confirmText: next ? 'Yes, Reactivate' : 'Yes, Inactivate',
    });
    if (!ok) return;
    setToggling(true);
    try {
      await riskImpactsService.toggleStatus(selectedImpact.id);
      setShowModal(false);
      setToast({ type: next ? 'success' : 'warning', message: next ? 'Impact Reactivated.' : 'Impact Inactivated.' });
      loadImpacts();
    } catch (err: any) {
      setToast({ type: 'error', message: extractApiError(err, 'Failed to update status') });
    } finally {
      setToggling(false);
    }
  };

  // Workflow (submit/approve/reject/reopen) is now driven end-to-end by the
  // unified <WorkflowModal> via the /api/v1/wf engine (kind="rsk_impact").

  const totalRecords = impacts.length;
  const paginatedRows = useMemo(
    () => impacts.slice((page - 1) * pageSize, page * pageSize),
    [impacts, page, pageSize],
  );

  if (!permsLoading && !canView) {
    return (
      <NoDataFound
        title="Not authorised"
        message={viewPerm.message || 'You are not authorised to view Risk Masters. Please contact your Super Admin.'}
      />
    );
  }

  return (
    <>
      <div className="scroll-container flex-1 overflow-hidden">
        <div className="table-list">
          <div className="table-list-scroll">
            <table className="table-list-table">
              <thead>
                <tr>
                  <th className="table-list-th table-list-cell--dot" aria-label="Workflow status"></th>
                  <th className="table-list-th table-list-cell--sno">S.No</th>
                  <th className="table-list-th table-list-cell--code">Code</th>
                  <th className="table-list-th">Impact Name</th>
                  <th className="table-list-th">Description</th>
                </tr>
              </thead>
              <tbody aria-busy={loading} aria-live="polite">
                {loading && <Skeleton.TableRows cols={['3xs', '3xs', 'xs', 'md', '3xs']} />}
                {!loading && impacts.length === 0 && (
                  <tr>
                    <td colSpan={5} className="table-list-cell">
                      <NoDataFound
                        title="No Risk Impacts Found"
                        message={canModify ? 'Click "Create" to add one.' : 'No records to display.'}
                      />
                    </td>
                  </tr>
                )}
                {!loading && paginatedRows.map((impact, idx) => (
                    <tr
                      key={impact.id}
                      className={`table-list-row${impact.is_active === 1 ? '' : ' is-inactive'}`}
                      data-testid={`BTN_risk_impact_row_${impact.id}_open`}
                      onClick={() => handleRowClick(impact)}
                    >
                      <td
                        className="table-list-cell table-list-cell--dot"
                        onClick={(e) => { e.stopPropagation(); setWorkflowOpenFor(impact); }}
                        title="Workflow — view timeline & act"
                        data-testid={`BTN_risk_impact_row_${impact.id}_workflow`}
                      >
                        <ApprovalDot state={impact.workflow_state} onClick={() => setWorkflowOpenFor(impact)} />
                      </td>
                      <td className="table-list-cell table-list-cell--sno">{(page - 1) * pageSize + idx + 1}</td>
                      <td className="table-list-cell table-list-cell--code">
                        <span className="cell-code">{(impact as any).code || impact.impact_code || '—'}</span>
                      </td>
                      <td className="table-list-cell">
                        <span className="table-list-cell-title cell-name-lock">
                          {impact.workflow_state === 'approved' && (
                            <Lock className="lock-icon lock-icon--sm" aria-label="Locked — approved" />
                          )}
                          {impact.impact_name}
                        </span>
                      </td>
                      <td className="table-list-cell table-list-cell--truncate" title={impact.impact_description || ''}>
                        {impact.impact_description || <span className="text-gray-400 italic">—</span>}
                      </td>
                    </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            page={page}
            pageSize={pageSize}
            totalRecords={totalRecords}
            onPageChange={setPage}
            onPageSizeChange={onPageSizeChange}
            recordLabel="impacts"
          />
        </div>
      </div>

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} size="md" data-testid="MOD_risk_impact_form">
        <Modal.Header>
          {isEditMode && selectedImpact
            ? <>{isApproved ? 'Viewing (locked)' : (canModify && isEditing) ? 'Editing' : 'Viewing'} "<span className="item-highlight">{selectedImpact.impact_name}</span>"</>
            : 'Create Risk Impact'}
        </Modal.Header>
        <Modal.Body>
          {isApproved && (
            <div className="approved-banner">
              <Lock size={14} /> Record is approved and locked. Reopen via the workflow button to edit.
            </div>
          )}
          <form id="impact-form" onSubmit={handleSubmit} className="flex flex-col gap-y-4">
            {/* System code — read-only, prefilled with the same code the list shows. */}
            {isEditMode && (
              <FormControl.Input
                label="Code"
                value={(selectedImpact as any)?.code || (selectedImpact as any)?.impact_code || '—'}
                disabled
                data-testid="INP_risk_impact_code"
              />
            )}
            <ValidatedInput
              vh={fieldVh}
              name="impact_name"
              code={109}
              required
              label="Impact Name"
              type="text"
              data-testid="INP_risk_impact_name"
              value={formData.impact_name || ''}
              onChange={e => setFormData({ ...formData, impact_name: e.target.value })}
              placeholder="e.g., Financial Loss, Reputational Damage"
              disabled={fieldsDisabled}
            />
            <ValidatedTextarea
              vh={fieldVh}
              name="impact_description"
              code={110}
              label="Description"
              data-testid="INP_risk_impact_description"
              value={formData.impact_description || ''}
              onChange={e => setFormData({ ...formData, impact_description: e.target.value })}
              rows={3}
              placeholder="Describe this risk impact…"
              disabled={fieldsDisabled}
            />
          </form>
        </Modal.Body>
        <Modal.Footer className={isEditMode ? 'app-modal-footer--split' : ''}>
          {isEditMode && selectedImpact && canModify && !isApproved && (
            <Button
              variant={selectedImpact.is_active ? 'inactive-outline' : 'active-outline'}
              icon={selectedImpact.is_active ? CircleMinus : CircleCheck}
              data-testid="BTN_risk_impact_toggle_active"
              onClick={handleToggleActive}
              disabled={toggling || submitting}
            >
              {toggling ? '...' : selectedImpact.is_active ? 'Inactive' : 'Active'}
            </Button>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            {/* Create → Submit. */}
            {!isEditMode && (
              <Button
                type="submit"
                form="impact-form"
                variant="fill"
                icon={Save}
                data-testid="BTN_risk_impact_submit"
                disabled={submitting || toggling}
              >
                {submitting ? 'Submitting...' : 'Submit'}
              </Button>
            )}
            {/* View mode → "Edit" enables the form (not for approved/locked). */}
            {isEditMode && canModify && !isApproved && !isEditing && (
              <Button
                variant="fill"
                icon={Edit2}
                data-testid="BTN_risk_impact_edit"
                onClick={() => setIsEditing(true)}
                disabled={submitting || toggling}
              >
                Edit
              </Button>
            )}
            {/* Edit mode → "Update" saves. */}
            {isEditMode && canModify && !isApproved && isEditing && (
              <Button
                type="submit"
                form="impact-form"
                variant="update"
                icon={Check}
                data-testid="BTN_risk_impact_update"
                disabled={submitting || toggling}
              >
                {submitting ? 'Updating...' : 'Update'}
              </Button>
            )}
          </div>
        </Modal.Footer>
      </Modal>

      <WorkflowModal
        isOpen={!!workflowOpenFor}
        onClose={() => setWorkflowOpenFor(null)}
        kind="rsk_impact"
        id={workflowOpenFor ? Number(workflowOpenFor.id) : null}
        onChanged={loadImpacts}
      />

      <Toast toast={toast} onClose={() => setToast(null)} />
    </>
  );
};

export default RiskImpactsTab;
